import type { TodayDefaultRole } from '@/lib/widgets/widget-definitions'
import { createLogger } from '@/lib/logger'
import { validPreviewLimit } from '@/lib/widgets/home-settings'
import {
  getOverviewPresentation,
  prioritizeDefaultOverview,
} from '@/lib/widgets/overview-layout'
import {
  getDefaultWidgetIds,
  WIDGET_DEFINITIONS,
} from '@/lib/widgets/widget-definitions'
import { useAuth as useAuthStore } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useUserPreferencesStore } from '@/store/user-preferences.store'
import { create } from 'zustand'

import {
  AccountType,
  isTodayLayoutData,
  isTodayLayoutPreferences,
  isTodayLegacyLayoutData,
  ModuleTypeEnum,
  PreferenceContextEnum,
  PreferenceScopeEnum,
  TodayLayoutData,
  TodayLayoutItem,
  UserPreferenceData,
} from '@scrambl/shared'

const logger = createLogger('store/today-layout.store')

const SIZE_ORDER: TodayLayoutItem['size'][] = ['small', 'medium', 'large']

const getDefaultSizeForWidget = (widgetId: string): TodayLayoutItem['size'] => {
  const def = WIDGET_DEFINITIONS.find(w => w.id === widgetId)
  if (def?.defaultSize) return def.defaultSize
  return def?.columnType === 'sidebar' ? 'small' : 'large'
}

const clampToMinSize = (
  widgetId: string,
  size: unknown,
): TodayLayoutItem['size'] => {
  const validSize = SIZE_ORDER.includes(size as TodayLayoutItem['size'])
    ? (size as TodayLayoutItem['size'])
    : getDefaultSizeForWidget(widgetId)
  const def = WIDGET_DEFINITIONS.find(w => w.id === widgetId)
  const minIdx = SIZE_ORDER.indexOf(def?.minSize ?? 'small')
  const maxIdx = SIZE_ORDER.indexOf(def?.maxSize ?? 'large')
  const sizeIdx = SIZE_ORDER.indexOf(validSize)
  if (sizeIdx < minIdx) return SIZE_ORDER[minIdx]
  if (sizeIdx > maxIdx) return SIZE_ORDER[maxIdx]
  return validSize
}

const GRID_COLUMNS = 3
const sizeToCols = (size: TodayLayoutItem['size']): number =>
  size === 'small' ? 1 : size === 'medium' ? 2 : 3

/**
 * Reorder a list of widgets so each row of the 3-column grid is filled as fully
 * as possible. Walks the queue in order; for each open row, prefers an exact-fit
 * widget over a first-fit one. Used during migration / initial-default generation
 * so users don't land on a layout with avoidable gaps. Does NOT run on
 * already-customized layouts — the user's chosen order is respected there.
 */
const packTightly = (items: TodayLayoutItem[]): TodayLayoutItem[] => {
  const queue = [...items]
  const result: TodayLayoutItem[] = []

  while (queue.length > 0) {
    let rowSpace = GRID_COLUMNS

    while (rowSpace > 0 && queue.length > 0) {
      // Prefer a widget that fills the remaining row exactly; otherwise the first that fits.
      let pickIdx = queue.findIndex(i => sizeToCols(i.size) === rowSpace)
      if (pickIdx === -1) {
        pickIdx = queue.findIndex(i => sizeToCols(i.size) <= rowSpace)
      }
      if (pickIdx === -1) break // nothing fits — wrap to next row

      const [picked] = queue.splice(pickIdx, 1)
      result.push(picked)
      rowSpace -= sizeToCols(picked.size)
    }
  }

  return result
}

// A widget belongs in an account type's default layout when it has no
// accountTypes gate (all accounts) or explicitly lists this account type. This
// keeps business widgets out of a personal default and vice-versa.
const isWidgetForAccountType = (
  w: (typeof WIDGET_DEFINITIONS)[number],
  accountType: AccountType,
): boolean => !w.accountTypes || w.accountTypes.includes(accountType)

/**
 * Which default widget set this member should be seeded with. Read from the
 * membership already on the current organization — no extra fetch, and no
 * effect whatsoever on a member who already has a saved layout.
 *
 * - External access (client-portal / contractor sessions) and GUEST
 *   memberships get the narrow contractor set.
 * - OWNER / ADMIN get the full manager board.
 * - Everyone else (MEMBER, VIEWER, unknown) gets the individual-contributor
 *   set — the manager board minus the cross-org context widgets.
 */
const getCurrentDefaultRole = (): TodayDefaultRole => {
  const org = useOrganizationStore.getState().currentOrganization
  if (org?.isExternalAccess === true) return 'contractor'
  const role = org?.userMembership?.role
  if (role === 'OWNER' || role === 'ADMIN') return 'manager'
  if (role === 'GUEST') return 'contractor'
  return 'member'
}

const generateDefaultLayout = (
  accountType: AccountType = AccountType.ORGANIZATION,
): TodayLayoutData => {
  // Personal spaces keep the untouched `isDefault` + accountTypes generation:
  // they render PersonalHomePage, not this grid, so role sets would be
  // inventing hierarchy nobody sees.
  const isPersonal = accountType === AccountType.PERSONAL
  const defaultIds = isPersonal
    ? new Set(WIDGET_DEFINITIONS.filter(w => w.isDefault).map(w => w.id))
    : new Set(getDefaultWidgetIds(getCurrentDefaultRole()))

  const items: TodayLayoutItem[] = []
  for (const w of WIDGET_DEFINITIONS) {
    if (
      defaultIds.has(w.id) &&
      w.columnType === 'main' &&
      isWidgetForAccountType(w, accountType)
    ) {
      items.push({ id: w.id, size: getDefaultSizeForWidget(w.id) })
    }
  }
  for (const w of WIDGET_DEFINITIONS) {
    if (
      defaultIds.has(w.id) &&
      w.columnType === 'sidebar' &&
      isWidgetForAccountType(w, accountType)
    ) {
      items.push({ id: w.id, size: getDefaultSizeForWidget(w.id) })
    }
  }

  if (isPersonal) return { items: packTightly(items) }

  return { items: prioritizeDefaultOverview(items) }
}

// Read the current account type at call time (store, not a hook). Defaults to
// ORGANIZATION when the org hasn't loaded — fail safe toward the full experience.
const getCurrentAccountType = (): AccountType =>
  (useOrganizationStore.getState().currentOrganization?.accountType as
    | AccountType
    | undefined) ?? AccountType.ORGANIZATION

// Account-type-aware default computed fresh so a personal space never falls back
// to a layout carrying business widgets (or vice-versa).
const getDefaultLayout = (): TodayLayoutData =>
  generateDefaultLayout(getCurrentAccountType())

const normalizeLayoutItems = (rawItems: unknown[]): TodayLayoutItem[] => {
  const seen = new Set<string>()
  return rawItems.flatMap(rawItem => {
    if (!rawItem || typeof rawItem !== 'object') return []
    const item = rawItem as Record<string, unknown>
    if (typeof item.id !== 'string' || !item.id.trim()) return []
    const id = item.id.trim()
    if (seen.has(id)) return []
    seen.add(id)
    const previewLimit = validPreviewLimit(item.previewLimit)
    return [
      {
        id,
        size: clampToMinSize(id, item.size),
        ...(typeof item.hidden === 'boolean' ? { hidden: item.hidden } : {}),
        ...(previewLimit ? { previewLimit } : {}),
        ...(typeof item.showCompleted === 'boolean'
          ? { showCompleted: item.showCompleted }
          : {}),
      },
    ]
  })
}

const migrateLayout = (raw: unknown): TodayLayoutData | null => {
  if (isTodayLayoutData(raw)) {
    const previewLimit = validPreviewLimit(raw.previewLimit)
    return {
      presentation: getOverviewPresentation(raw.presentation),
      ...(previewLimit ? { previewLimit } : {}),
      // Unknown IDs survive: a future widget or another account's gated widget
      // must not lose its saved position/settings just because it cannot render.
      items: normalizeLayoutItems(raw.items),
    }
  }
  if (isTodayLegacyLayoutData(raw)) {
    const items = normalizeLayoutItems(
      [...raw.main, ...raw.sidebar].map(id => ({ id })),
    )
    return { items: packTightly(items) }
  }
  return null
}

interface LayoutIdentity {
  userId: string
  organizationId: string
}
interface LayoutOperation {
  identity: LayoutIdentity
  generation: number
}
let resetGeneration = 0
let activeFetch: LayoutOperation | null = null
let lastPreferencesIdentity: string | null = null
let lastPreferencesUpdateTimestamp = Number.NEGATIVE_INFINITY

const getIdentity = (): LayoutIdentity | null => {
  const userId = useAuthStore.getState().user?.id
  const organizationId = useOrganizationStore.getState().currentOrganization?.id
  return userId && organizationId ? { userId, organizationId } : null
}
const identityKey = (identity: LayoutIdentity) =>
  `${identity.userId}:${identity.organizationId}`
const isCurrentOperation = (operation: LayoutOperation): boolean => {
  const current = getIdentity()
  return (
    operation.generation === resetGeneration &&
    !!current &&
    identityKey(current) === identityKey(operation.identity)
  )
}
const matchesIdentity = (
  preference: UserPreferenceData,
  identity: LayoutIdentity,
) =>
  preference.organizationId === identity.organizationId &&
  (preference.userId === identity.userId ||
    (preference.userId === null &&
      preference.scope === PreferenceScopeEnum.ORGANIZATION))

const preferencesAreReady = (
  state: {
    isInitialized: boolean
    isLoading: boolean
    preferences: UserPreferenceData[]
  },
  identity: LayoutIdentity,
) =>
  state.isInitialized &&
  !state.isLoading &&
  (state.preferences.length === 0 ||
    state.preferences.some(preference => matchesIdentity(preference, identity)))

const rememberPreferenceTimestamp = (
  preference: UserPreferenceData,
  identity: LayoutIdentity,
) => {
  const scope = identityKey(identity)
  if (lastPreferencesIdentity !== scope) {
    lastPreferencesIdentity = scope
    lastPreferencesUpdateTimestamp = Number.NEGATIVE_INFINITY
  }
  const timestamp = new Date(preference.updatedAt ?? 0).getTime()
  if (Number.isFinite(timestamp)) {
    lastPreferencesUpdateTimestamp = Math.max(
      lastPreferencesUpdateTimestamp,
      timestamp,
    )
  }
}

const findScopedPreference = (
  preferences: UserPreferenceData[],
  identity: LayoutIdentity,
) =>
  preferences
    .filter(
      preference =>
        preference.context === PreferenceContextEnum.LAYOUT &&
        preference.moduleType === ModuleTypeEnum.TODAY &&
        preference.isActive !== false &&
        matchesIdentity(preference, identity),
    )
    .sort((a, b) => {
      const aUser = a.userId === identity.userId
      const bUser = b.userId === identity.userId
      if (aUser !== bUser) return aUser ? -1 : 1
      return (
        new Date(b.updatedAt ?? 0).getTime() -
        new Date(a.updatedAt ?? 0).getTime()
      )
    })[0]

const layoutsMatch = (first: TodayLayoutData | null, second: TodayLayoutData) =>
  JSON.stringify(migrateLayout(first)) === JSON.stringify(migrateLayout(second))

/* ===== Last-known layout snapshot =====
 *
 * The widget grid can't render at all until the layout resolves, and the
 * layout lives behind the user-preferences fetch — so even with every widget
 * query restored from cache, a cold open is a full-page skeleton for as long
 * as that round trip takes. Keeping the last-known layout in localStorage lets
 * the grid paint immediately and swap in the server layout when it lands.
 *
 * Keyed by user AND org: the layout differs per org, and an org switch must
 * never flash the previous org's widget set. Capped at 24h so an abandoned
 * machine doesn't resurrect a long-dead dashboard.
 */
const SNAPSHOT_KEY_PREFIX = 'scrambl:today-layout:'
const SNAPSHOT_MAX_AGE_MS = 1000 * 60 * 60 * 24

interface TodayLayoutSnapshot {
  storedAt: number
  layout: TodayLayoutData
}

const getSnapshotKey = (identity = getIdentity()): string | null => {
  if (typeof window === 'undefined' || !identity) return null
  return `${SNAPSHOT_KEY_PREFIX}${identityKey(identity)}`
}

/** Last-known layout for the current user+org, or null when there is none / it aged out. */
export const readTodayLayoutSnapshot = (): TodayLayoutData | null => {
  const key = getSnapshotKey()
  if (!key) return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as TodayLayoutSnapshot
    if (
      typeof parsed?.storedAt !== 'number' ||
      !Number.isFinite(parsed.storedAt) ||
      Date.now() - parsed.storedAt > SNAPSHOT_MAX_AGE_MS
    ) {
      window.localStorage.removeItem(key)
      return null
    }
    // Run it through the same migration as a server payload — the widget
    // catalogue may have changed shape since the snapshot was written.
    const migrated = migrateLayout(parsed.layout)
    return migrated
  } catch {
    return null
  }
}

const writeTodayLayoutSnapshot = (
  layout: TodayLayoutData,
  operation: LayoutOperation,
): void => {
  if (!isCurrentOperation(operation)) return
  const key = getSnapshotKey(operation.identity)
  if (!key) return
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        storedAt: Date.now(),
        layout,
      } satisfies TodayLayoutSnapshot),
    )
  } catch {
    // Quota / private mode — the snapshot is best-effort, never load-bearing.
  }
}

/** Drop every stored layout snapshot. Called at sign-out, alongside the other
 *  persisted-store cleanup, so the next account on this machine starts clean. */
export const clearTodayLayoutSnapshots = (): void => {
  if (typeof window === 'undefined') return
  try {
    const keys: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key?.startsWith(SNAPSHOT_KEY_PREFIX)) keys.push(key)
    }
    keys.forEach(key => window.localStorage.removeItem(key))
  } catch {
    // Best-effort.
  }
}

interface TodayLayoutState {
  layout: TodayLayoutData | null
  isLoading: boolean
  isSaving: boolean
  error: Error | null
  fetchLayout: () => Promise<void>
  saveLayout: (
    newLayoutConfig: TodayLayoutData,
    expectedLayout?: TodayLayoutData,
  ) => Promise<void>
  reset: () => void
}

export const useTodayLayout = create<TodayLayoutState>((set, get) => ({
  layout: null,
  isLoading: false,
  isSaving: false,
  error: null,
  fetchLayout: async () => {
    const identity = getIdentity()
    if (!identity || (activeFetch && isCurrentOperation(activeFetch))) return
    const operation = { identity, generation: resetGeneration }
    activeFetch = operation
    const snapshot = get().layout ? null : readTodayLayoutSnapshot()
    set({
      isLoading: true,
      error: null,
      ...(snapshot ? { layout: snapshot } : {}),
    })

    try {
      let ready = false
      for (let attempt = 0; attempt < 10; attempt += 1) {
        if (!isCurrentOperation(operation)) return
        const preferences = useUserPreferencesStore.getState()
        if (preferencesAreReady(preferences, identity)) {
          ready = true
          break
        }
        if (attempt < 9) await new Promise(resolve => setTimeout(resolve, 200))
      }
      if (!isCurrentOperation(operation)) return
      if (!ready) {
        // A timeout fallback never overwrites a confirmed snapshot.
        set({
          layout: get().layout ?? getDefaultLayout(),
          error: new Error(
            'Customize is still unavailable. Please try syncing again.',
          ),
        })
        return
      }
      const preferences = useUserPreferencesStore.getState()
      const preference = findScopedPreference(preferences.preferences, identity)
      const migrated =
        preference?.preferences &&
        isTodayLayoutPreferences(preference.preferences)
          ? migrateLayout(preference.preferences.layout)
          : null
      const layout = migrated ?? getDefaultLayout()
      if (!isCurrentOperation(operation)) return
      writeTodayLayoutSnapshot(layout, operation)
      if (preference && migrated)
        rememberPreferenceTimestamp(preference, identity)
      set({ layout })
    } catch (error) {
      if (!isCurrentOperation(operation)) return
      logger.error('TodayLayoutStore: Error fetching layout:', error)
      set({ error: error as Error, layout: get().layout ?? getDefaultLayout() })
    } finally {
      if (activeFetch === operation) {
        activeFetch = null
        if (isCurrentOperation(operation)) set({ isLoading: false })
      }
    }
  },
  saveLayout: async (newLayoutConfig, expectedLayout) => {
    const identity = getIdentity()
    const preferences = useUserPreferencesStore.getState()
    if (
      !identity ||
      !preferencesAreReady(preferences, identity) ||
      get().isLoading
    ) {
      throw new Error(
        'Please wait for your Home preferences to finish loading before saving.',
      )
    }
    if (get().isSaving) {
      throw new Error(
        'Please wait for your current layout change to finish saving.',
      )
    }
    if (expectedLayout && !layoutsMatch(get().layout, expectedLayout)) {
      throw new Error(
        'Your home changed while Customize was open. Reopen it to use the latest version.',
      )
    }
    const operation = { identity, generation: resetGeneration }
    const layoutToSave = migrateLayout({ ...get().layout, ...newLayoutConfig })
    if (!layoutToSave) throw new Error('The Home layout is not valid.')
    set({ error: null, isSaving: true })
    try {
      const response = await preferences.savePreference(
        PreferenceContextEnum.LAYOUT,
        { layout: layoutToSave },
        ModuleTypeEnum.TODAY,
      )
      if (!isCurrentOperation(operation)) {
        throw new Error(
          'Your account or workspace changed before this layout finished saving.',
        )
      }
      if (
        !response ||
        !findScopedPreference([response], identity) ||
        !isTodayLayoutPreferences(response.preferences)
      ) {
        throw new Error(
          'Your Home layout could not be confirmed. Please try saving again.',
        )
      }
      const confirmedLayout = migrateLayout(response.preferences.layout)
      if (!confirmedLayout)
        throw new Error(
          'Your Home layout could not be confirmed. Please try saving again.',
        )
      writeTodayLayoutSnapshot(confirmedLayout, operation)
      rememberPreferenceTimestamp(response, identity)
      set({ layout: confirmedLayout })
    } catch (error) {
      if (isCurrentOperation(operation)) {
        logger.error('TodayLayoutStore: Error saving layout:', error)
        set({ error: error as Error })
      }
      throw error
    } finally {
      if (isCurrentOperation(operation)) set({ isSaving: false })
    }
  },
  reset: () => {
    resetGeneration += 1
    activeFetch = null
    lastPreferencesIdentity = null
    lastPreferencesUpdateTimestamp = Number.NEGATIVE_INFINITY
    set({ layout: null, isLoading: false, isSaving: false, error: null })
  },
}))

// A late preference notification from another account/workspace cannot restore
// its layout or advance the current workspace's subscription timestamp.
useUserPreferencesStore.subscribe(state => {
  const identity = getIdentity()
  if (!identity || !state.isInitialized || state.isLoading) return
  const scope = identityKey(identity)
  if (lastPreferencesIdentity !== scope) {
    lastPreferencesIdentity = scope
    lastPreferencesUpdateTimestamp = Number.NEGATIVE_INFINITY
  }
  const todayPreference = findScopedPreference(state.preferences, identity)
  if (
    !todayPreference?.updatedAt ||
    !isTodayLayoutPreferences(todayPreference.preferences)
  )
    return
  const updateTime = new Date(todayPreference.updatedAt).getTime()
  if (
    !Number.isFinite(updateTime) ||
    updateTime <= lastPreferencesUpdateTimestamp
  )
    return
  const layout = migrateLayout(todayPreference.preferences.layout)
  if (!layout) return
  lastPreferencesUpdateTimestamp = updateTime
  if (!layoutsMatch(useTodayLayout.getState().layout, layout)) {
    writeTodayLayoutSnapshot(layout, { identity, generation: resetGeneration })
    useTodayLayout.setState({ layout })
  }
})

export type { TodayLayoutData, TodayLayoutItem }
